// M004-F0749 (WebUI #191, design docs/design/M004-F0749-Invoices-Tab.md). The Invoices tab on the Ledger landing page: every ISSUED
// invoice of one organisation, newest first, with Download PDF and an Open drawer. Coded to Spec core-ledger-account-gateway 1.14.0
// (invoice.list / invoice.get / invoice.pdf). The caller (the landing page) has already gated the tab and the organisation list on
// billing_transactions_view or ledger_manage; Core and the Ledger enforce the same gate. Nothing is shown that the Ledger did not send.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { invoiceApi } from '../../services/invoiceApi';
import InvoiceDrawer from './InvoiceDrawer';
import { downloadInvoicePdf } from './invoicePdf';
import {
  cacheGet, cachePut, deliveryText, errorInfo, filterInvoices, formatDay, hasFilters, money, monthLabel, monthOptions, sortNewest, statusText,
} from './invoiceModel';
import './InvoicesTab.css';

const PAGE = 200;
const MAX_PAGES = 25;
const listOf = (response) => {
  const data = response?.data?.data;
  if (!data || !Array.isArray(data.invoices)) throw new Error('The Ledger answered with an invoice list this page cannot read.');
  return data;
};

async function loadAll(orgId) {
  const rows = [];
  let offset = 0;
  for (let page = 0; page < MAX_PAGES; page += 1) {
    const data = listOf(await invoiceApi.list(orgId, { limit: PAGE, offset }));
    rows.push(...data.invoices);
    if (data.next_offset === null || data.next_offset === undefined) return rows;
    offset = data.next_offset;
  }
  return rows;
}

const when = (iso) => new Date(iso).toLocaleString('en-GB');

// The open drawer is component state, not a URL member: the shared history drawer rewrites the query from its own snapshot of the
// location when a TXN is opened, so a URL-held `invoice` would be written back and the two drawers would stack.
export default function InvoicesTab({ organisations = [], orgId, onSelectOrg }) {
  const [invoiceId, setInvoiceId] = useState(null);
  const [state, setState] = useState({ status: 'loading', rows: [], error: null, cachedAt: null });
  const [filters, setFilters] = useState({ customer: '', number: '', month: '' });
  const [pdfError, setPdfError] = useState('');
  const [busyId, setBusyId] = useState('');
  const request = useRef(0);
  const org = organisations.find((item) => String(item.id) === String(orgId));
  const orgName = org?.name || `organisation ${orgId}`;

  const load = useCallback(async () => {
    const mine = request.current + 1;
    request.current = mine;
    setState({ status: 'loading', rows: [], error: null, cachedAt: null });
    setPdfError('');
    try {
      const rows = sortNewest(await loadAll(orgId));
      if (request.current !== mine) return;
      cachePut(orgId, rows);
      setState({ status: 'ready', rows, error: null, cachedAt: null });
    } catch (cause) {
      if (request.current !== mine) return;
      const error = errorInfo(cause);
      if (error.unsupported) { setState({ status: 'unsupported', rows: [], error, cachedAt: null }); return; }
      const copy = error.unreachable ? cacheGet(orgId) : null;
      if (copy) setState({ status: 'cached', rows: copy.rows, error, cachedAt: copy.at });
      else setState({ status: 'error', rows: [], error, cachedAt: null });
    }
  }, [orgId]);

  useEffect(() => { setFilters({ customer: '', number: '', month: '' }); setInvoiceId(null); if (orgId) load(); }, [orgId, load]);

  const download = async (row) => {
    setPdfError('');
    setBusyId(row.invoice_id);
    try { await downloadInvoicePdf(orgId, row); } catch (cause) { const e = errorInfo(cause); setPdfError(`${row.number}: ${e.code ? `${e.code}. ` : ''}${e.message}`); } finally { setBusyId(''); }
  };

  const shown = filterInvoices(state.rows, filters);
  const ledgerDown = state.status === 'cached';
  const showList = state.status === 'ready' || ledgerDown;
  const setFilter = (key) => (event) => setFilters((current) => ({ ...current, [key]: event.target.value }));
  const openRow = state.rows.find((row) => row.invoice_id === invoiceId) || null;

  return (
    <section className="inv-tab" aria-labelledby="inv-tab-title" data-testid="invoices-tab">
      <header className="inv-header">
        <div>
          <span className="inv-eyebrow">Ledger</span>
          <h2 id="inv-tab-title">Invoices</h2>
          <p className="inv-copy">Every invoice issued for the organisation, newest first. The PDF is the one stored when the invoice was issued.</p>
        </div>
        <label className="inv-org-select">Organisation
          <select value={String(orgId)} onChange={(event) => onSelectOrg?.(event.target.value)}>
            {organisations.map((item) => <option key={item.id} value={String(item.id)}>{item.name}</option>)}
          </select>
        </label>
      </header>

      {state.status === 'loading' ? (
        <div className="inv-skeleton" data-testid="invoices-skeleton" role="status" aria-busy="true" aria-label="Loading invoices">
          {[0, 1, 2, 3].map((n) => <div className="inv-skeleton-row" key={n} aria-hidden="true" />)}
          <span className="inv-sr">Loading invoices…</span>
        </div>
      ) : null}

      {state.status === 'error' ? (
        <div className="inv-error" role="alert" data-testid="invoices-error">
          <strong>The invoices could not be loaded.</strong>
          <span>{state.error.code ? `${state.error.code}: ` : ''}{state.error.message}</span>
          <button type="button" onClick={load}>Retry</button>
        </div>
      ) : null}

      {state.status === 'unsupported' ? (
        <p className="inv-state" role="status" data-testid="invoices-unsupported">Invoices are not available from this Ledger yet.</p>
      ) : null}

      {ledgerDown ? (
        <p className="inv-cached" role="status" data-testid="invoices-cached">
          Showing the last copy from {when(state.cachedAt)}; the Ledger is not reachable ({state.error.code || 'no answer'}). PDFs cannot be downloaded until it is back.
          {' '}<button type="button" onClick={load}>Try again</button>
        </p>
      ) : null}

      {pdfError ? <p className="inv-error" role="alert" data-testid="invoice-pdf-error">{pdfError}</p> : null}

      {showList && state.rows.length === 0 ? (
        <p className="inv-state" data-testid="invoices-empty">No invoices have been issued for {orgName} yet.</p>
      ) : null}

      {showList && state.rows.length > 0 ? (
        <>
          <form className="inv-filters" role="search" aria-label="Filter invoices" onSubmit={(event) => event.preventDefault()}>
            <label>Customer<input type="search" value={filters.customer} onChange={setFilter('customer')} placeholder="Name" /></label>
            <label>Invoice number<input type="search" value={filters.number} onChange={setFilter('number')} placeholder="DAH-00001" /></label>
            <label>Month
              <select value={filters.month} onChange={setFilter('month')}>
                <option value="">All months</option>
                {monthOptions(state.rows).map((month) => <option key={month} value={month}>{monthLabel(month)}</option>)}
              </select>
            </label>
            {hasFilters(filters) ? <button type="button" onClick={() => setFilters({ customer: '', number: '', month: '' })}>Clear filters</button> : null}
          </form>
          <p className="inv-count" role="status">{shown.length} of {state.rows.length} {state.rows.length === 1 ? 'invoice' : 'invoices'}</p>
          {shown.length === 0 ? (
            <p className="inv-state" data-testid="invoices-no-match">No invoices match these filters.</p>
          ) : (
            <table className="inv-table" data-testid="invoices-table">
              <caption className="inv-sr">Issued invoices, newest first</caption>
              <thead>
                <tr><th scope="col">Number</th><th scope="col">Customer</th><th scope="col">Issued</th><th scope="col">Due</th><th scope="col" className="inv-num">Total</th><th scope="col">Status</th><th scope="col">Delivery</th><th scope="col">Actions</th></tr>
              </thead>
              <tbody>
                {shown.map((row) => (
                  <tr key={row.invoice_id} data-testid="invoice-row">
                    <th scope="row" data-label="Number" data-testid="invoice-number">{row.number}</th>
                    <td data-label="Customer">{row.customer_name}</td>
                    <td data-label="Issued">{formatDay(row.issue_date)}</td>
                    <td data-label="Due">{formatDay(row.due_date)}</td>
                    <td data-label="Total" className="inv-num">{money(row.total_minor, row.currency)}</td>
                    <td data-label="Status"><span className="inv-pill">{statusText(row.status)}</span></td>
                    <td data-label="Delivery" data-testid="invoice-delivery">{deliveryText(row.delivery)}</td>
                    <td data-label="Actions" className="inv-actions">
                      <button type="button" disabled={ledgerDown || busyId === row.invoice_id} aria-label={`Download PDF for ${row.number}`} onClick={() => download(row)}>
                        {busyId === row.invoice_id ? 'Preparing…' : 'Download PDF'}
                      </button>
                      <button type="button" aria-label={`Open invoice ${row.number}`} onClick={() => setInvoiceId(row.invoice_id)}>Open</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      ) : null}

      {invoiceId ? (
        <InvoiceDrawer
          orgId={orgId}
          invoiceId={invoiceId}
          summary={openRow}
          ledgerDown={ledgerDown}
          onClose={() => setInvoiceId(null)}
          onDownload={download}
        />
      ) : null}
    </section>
  );
}
